import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, Filter, RotateCcw, Search } from "lucide-react";
import api from "@/lib/api";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtInt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);

const RangeControl = ({ label, unit, min, max, step, value, onChange, testId, formatter }) => {
  const [v1, v2] = value;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
        <div className="text-[11px] text-slate-700 font-mono-num">
          {formatter ? formatter(v1) : v1}
          <span className="text-slate-400 mx-1">→</span>
          {formatter ? formatter(v2) : v2}
        </div>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[v1, v2]}
        onValueChange={onChange}
        data-testid={testId}
        className="mt-1"
      />
    </div>
  );
};

const FiltersSidebar = ({ filters, setFilters, facets, onReset, boardId, collapsed = false, onToggleCollapsed = () => {} }) => {
  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const [ranges, setRanges] = useState({ mq: [30, 200], prezzo: [50000, 500000], eur_mq: [1000, 6000] });
  const [mqRange, setMqRange] = useState([30, 200]);
  const [prezzoRange, setPrezzoRange] = useState([50000, 500000]);
  const [eurMqRange, setEurMqRange] = useState([1000, 6000]);

  useEffect(() => {
    if (!boardId) return;
    (async () => {
      try {
        const r = await api.get("/properties/ranges", { params: { board_id: boardId } });
        setRanges(r.data);
        setMqRange([Number(filters.mq_min) || r.data.mq[0], Number(filters.mq_max) || r.data.mq[1]]);
        setPrezzoRange([Number(filters.prezzo_min) || r.data.prezzo[0], Number(filters.prezzo_max) || r.data.prezzo[1]]);
        setEurMqRange([Number(filters.eur_mq_min) || r.data.eur_mq[0], Number(filters.eur_mq_max) || r.data.eur_mq[1]]);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  // Sync slider values back to filters (only when user touches slider)
  const commitMq = (v) => {
    setMqRange(v);
    setFilters((f) => ({
      ...f,
      mq_min: v[0] === ranges.mq[0] ? "" : String(v[0]),
      mq_max: v[1] === ranges.mq[1] ? "" : String(v[1]),
    }));
  };
  const commitPrezzo = (v) => {
    setPrezzoRange(v);
    setFilters((f) => ({
      ...f,
      prezzo_min: v[0] === ranges.prezzo[0] ? "" : String(v[0]),
      prezzo_max: v[1] === ranges.prezzo[1] ? "" : String(v[1]),
    }));
  };
  const commitEurMq = (v) => {
    setEurMqRange(v);
    setFilters((f) => ({
      ...f,
      eur_mq_min: v[0] === ranges.eur_mq[0] ? "" : String(v[0]),
      eur_mq_max: v[1] === ranges.eur_mq[1] ? "" : String(v[1]),
    }));
  };

  const doReset = () => {
    setMqRange(ranges.mq);
    setPrezzoRange(ranges.prezzo);
    setEurMqRange(ranges.eur_mq);
    onReset();
  };

  return (
    <div className="ios-card overflow-hidden" data-testid="filters-card">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors"
        onClick={onToggleCollapsed}
        data-testid="filters-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center text-white shrink-0">
            <Filter className="w-3.5 h-3.5" strokeWidth={2} />
          </div>
          <div className="min-w-0 text-left">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold leading-none">Filtri</div>
            <div className="font-display font-bold text-sm leading-tight text-slate-800 mt-0.5">Affina i risultati</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); doReset(); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); doReset(); } }}
            data-testid="filters-reset-btn"
            className="rounded-full h-6 w-6 p-0 hover:bg-slate-100 inline-flex items-center justify-center cursor-pointer"
            title="Ripristina filtri"
          >
            <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
          </span>
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform ${collapsed ? "-rotate-90" : ""}`}
            strokeWidth={2}
          />
        </div>
      </button>

      {!collapsed && (
        <>
        <div className="px-3 pb-3 space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-yellow-50 border border-yellow-200 px-3 py-2">
            <Label className="text-[11px] uppercase tracking-wider text-yellow-800 font-semibold flex items-center gap-1.5 cursor-pointer" htmlFor="filter-only-fav">
              <span className="w-3.5 h-3.5 inline-block">
                <img src="/board-icons/star.png" alt="" className="w-3.5 h-3.5 object-contain" />
              </span>
              Solo preferiti
            </Label>
            <Checkbox
              id="filter-only-fav"
              data-testid="filter-only-favorites"
              checked={!!filters.favorites_only}
              onCheckedChange={(v) => upd("favorites_only", !!v)}
              className="h-4 w-4 rounded border-yellow-400 data-[state=checked]:bg-[#F5B800] data-[state=checked]:border-[#F5B800]"
            />
          </div>

          <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
            <Label className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold flex items-center gap-1.5 cursor-pointer" htmlFor="filter-only-dups">
              <img src="/board-icons/flame.png" alt="" aria-hidden="true" className="w-4 h-4 object-contain" /> Solo duplicati
            </Label>
            <Checkbox
              id="filter-only-dups"
              data-testid="filter-only-duplicates"
              checked={!!filters.only_duplicates}
              onCheckedChange={(v) => upd("only_duplicates", !!v)}
              className="h-4 w-4 rounded border-amber-400 data-[state=checked]:bg-[#FF9500] data-[state=checked]:border-[#FF9500]"
            />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Ricerca</Label>
            <div className="relative mt-1.5">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="filter-q"
                value={filters.q || ""}
                onChange={(e) => upd("q", e.target.value)}
                placeholder="Via, città, agenzia…"
                className="rounded-xl h-9 pl-9 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Macro area</Label>
              {Array.isArray(filters.macro_area) && filters.macro_area.length > 0 && (
                <button
                  type="button"
                  onClick={() => upd("macro_area", [])}
                  data-testid="filter-macro-area-clear"
                  className="text-[10px] text-slate-500 hover:text-[#007AFF] uppercase tracking-wider"
                >
                  Azzera
                </button>
              )}
            </div>
            {(facets.macro_area || []).length === 0 ? (
              <div className="text-[11px] text-slate-400 mt-1.5 italic">Nessuna macro area disponibile.</div>
            ) : (
              <div
                className="mt-1.5 max-h-48 overflow-y-auto rounded-xl bg-slate-100 border border-slate-200/60 p-1"
                data-testid="filter-macro-area"
              >
                {(facets.macro_area || []).map((c) => {
                  const arr = Array.isArray(filters.macro_area) ? filters.macro_area : [];
                  const checked = arr.includes(c);
                  const toggle = () => {
                    const next = checked ? arr.filter((x) => x !== c) : [...arr, c];
                    upd("macro_area", next);
                  };
                  return (
                    <label
                      key={c}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors text-xs ${
                        checked ? "bg-white text-slate-900 shadow-sm" : "hover:bg-white/70 text-slate-700"
                      }`}
                      data-testid={`filter-macro-area-item-${c}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={toggle}
                        className="h-3.5 w-3.5 rounded border-slate-300 data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]"
                      />
                      <span className="uppercase tracking-wide truncate">{c}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {Array.isArray(filters.macro_area) && filters.macro_area.length > 0 && (
              <div className="mt-1.5 text-[10px] text-slate-500 font-mono-num">
                {filters.macro_area.length} selezionat{filters.macro_area.length === 1 ? "a" : "e"}
              </div>
            )}
          </div>
        </div>

      <div className="px-3 pb-3 pt-4 border-t border-slate-100 space-y-5">
        <RangeControl
          label="Metri quadri"
          min={ranges.mq[0]}
          max={ranges.mq[1]}
          step={1}
          value={mqRange}
          onChange={commitMq}
          testId="filter-mq-slider"
          formatter={(v) => `${fmtInt(v)} m²`}
        />
        <div className="grid grid-cols-5 gap-1">
          {[
            ["30-50", 30, 50],
            ["51-70", 51, 70],
            ["71-90", 71, 90],
            ["91-110", 91, 110],
            [">110", 111, ranges.mq[1]],
          ].map(([label, mn, mx]) => (
            <button
              key={label}
              onClick={() => commitMq([mn, mx])}
              data-testid={`filter-mq-preset-${label}`}
              className="text-[10px] font-mono-num tracking-wide px-1 py-1.5 rounded-lg bg-slate-100 hover:bg-[#007AFF] hover:text-white transition-colors"
            >{label}</button>
          ))}
        </div>

        <RangeControl
          label="Prezzo"
          min={ranges.prezzo[0]}
          max={ranges.prezzo[1]}
          step={1000}
          value={prezzoRange}
          onChange={commitPrezzo}
          testId="filter-prezzo-slider"
          formatter={fmtEuro}
        />

        <RangeControl
          label="€ / m²"
          min={ranges.eur_mq[0]}
          max={ranges.eur_mq[1]}
          step={50}
          value={eurMqRange}
          onChange={commitEurMq}
          testId="filter-eurmq-slider"
          formatter={fmtEuro}
        />
      </div>

      <div className="px-3 pb-3 pt-4 border-t border-slate-100 space-y-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Da ristrutturare</Label>
          <Select value={filters.da_ristrutturare || "__all"} onValueChange={(v) => upd("da_ristrutturare", v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="filter-ristrutturare" className="rounded-xl mt-1.5 h-9 bg-slate-100 border-0">
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tutti</SelectItem>
              <SelectItem value="SI">Sì</SelectItem>
              <SelectItem value="NO">No</SelectItem>
              <SelectItem value="NI">NI (Non Indicato)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Ascensore</Label>
          <Select value={filters.ascensore || "__all"} onValueChange={(v) => upd("ascensore", v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="filter-ascensore" className="rounded-xl mt-1.5 h-9 bg-slate-100 border-0">
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tutti</SelectItem>
              <SelectItem value="true">Sì</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Tipo venditore</Label>
          <Select value={filters.tipo_venditore || "__all"} onValueChange={(v) => upd("tipo_venditore", v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="filter-tipo-venditore" className="rounded-xl mt-1.5 h-9 bg-slate-100 border-0">
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tutti</SelectItem>
              <SelectItem value="AGENZIA">Agenzia</SelectItem>
              <SelectItem value="PRIVATO">Privato</SelectItem>
            </SelectContent>
          </Select>
        </div>
        </div>
        </>
      )}
    </div>
  );
};

export default FiltersSidebar;
